import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/_services/api.service';
import { MessageService } from 'primeng/api';
import { PLAYLIST_FERMA_MINUTI_DEFAULT } from '../monitor-playlist-alert.util';

@Component({
  selector: 'app-monitor-setup',
  templateUrl: './monitor-setup.component.html',
  styleUrls: ['./monitor-setup.component.css'],
  providers: [MessageService],
})
export class MonitorSetupComponent implements OnInit {
  activeTab = 0;
  isLoading = true;
  isSaving = false;
  isTesting = false;
  smtpPassword = '';
  smtpPasswordSet = false;
  testEmail = '';
  alertAbilitato = false;
  alertPlaylistAbilitato = false;
  readonly emailPlaceholdersHelp =
    'Placeholder: {monitor_descrizione}, {indirizzo_ip}, {minuti}, {last_daemon_at}, {struttura}';
  readonly emailPlaylistPlaceholdersHelp =
    'Placeholder: {monitor_descrizione}, {indirizzo_ip}, {playlist_nome}, {minuti}, {last_signage_at}, {struttura}';

  isLoadingRelease = false;
  isUploadingRelease = false;
  signageRelease: any = null;
  releaseVersione = '';
  releaseFile: File | null = null;

  encryptionOptions = [
    { label: 'Nessuna', value: 'none' },
    { label: 'TLS', value: 'tls' },
    { label: 'SSL', value: 'ssl' },
  ];

  setup: any = {
    alert_abilitato: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_encryption: 'tls',
    smtp_user: '',
    email_from: '',
    email_from_name: 'Monitor Alert',
    email_destinatari: '',
    minuti_inattivita: 5,
    ripeti_ogni_minuti: 60,
    oggetto_email: 'Monitor offline: {monitor_descrizione}',
    corpo_email:
      'Il monitor {monitor_descrizione} ({indirizzo_ip}) non risponde da oltre {minuti} minuti.\nUltimo segnale: {last_daemon_at}',
    alert_playlist_abilitato: false,
    minuti_playlist_ferma: PLAYLIST_FERMA_MINUTI_DEFAULT,
    ripeti_ogni_minuti_playlist: 60,
    oggetto_email_playlist: 'Playlist ferma: {monitor_descrizione}',
    corpo_email_playlist:
      'Il monitor {monitor_descrizione} ({indirizzo_ip}) è online ma la playlist "{playlist_nome}" non aggiorna da oltre {minuti} minuti.\nUltimo contatto signage: {last_signage_at}',
  };

  constructor(
    private apiService: ApiService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadSetup();
    this.loadSignageRelease();
  }

  loadSetup(showSpinner = true): void {
    if (showSpinner) {
      this.isLoading = true;
    }
    this.apiService.post('kodi/monitor_setup_get', {}).subscribe(
      (data: any) => {
        const row = this.extractSetupRow(data);
        if (row) {
          this.applySetup(row);
        }
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this.addMessage('error', 'Errore', 'Impossibile caricare le impostazioni');
      }
    );
  }

  loadSignageRelease(): void {
    this.isLoadingRelease = true;
    this.apiService.post('kodi/signage_release_get', {}).subscribe(
      (data: any) => {
        this.signageRelease = this.extractResultItem(data);
        this.isLoadingRelease = false;
      },
      () => {
        this.isLoadingRelease = false;
        this.signageRelease = null;
        this.addMessage('error', 'Errore', 'Impossibile caricare la release signage');
      }
    );
  }

  onReleaseFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    if (!file) {
      this.releaseFile = null;
      return;
    }
    if (!/\.zip$/i.test(file.name)) {
      this.addMessage('warn', 'Attenzione', 'Selezionare un file .zip');
      input.value = '';
      this.releaseFile = null;
      return;
    }
    this.releaseFile = file;
  }

  uploadSignageRelease(): void {
    if (!this.releaseFile) {
      this.addMessage('warn', 'Attenzione', 'Selezionare un file .zip');
      return;
    }

    const formData = new FormData();
    formData.append('file', this.releaseFile, this.releaseFile.name);
    if (this.releaseVersione?.trim()) {
      formData.append('versione', this.releaseVersione.trim());
    }

    this.isUploadingRelease = true;
    this.apiService.postFormData('kodi/signage_release_upload', formData).subscribe(
      (data: any) => {
        this.isUploadingRelease = false;
        const result = this.extractResultItem(data);
        const messaggio = result?.messaggio || result?.message || 'Release caricata';
        if (result?.esito === 'errore' || data?.Success === false) {
          this.addMessage('error', 'Errore', messaggio);
          return;
        }
        this.addMessage('success', 'Successo', messaggio);
        this.releaseFile = null;
        this.releaseVersione = '';
        this.loadSignageRelease();
      },
      () => {
        this.isUploadingRelease = false;
        this.addMessage('error', 'Errore', 'Upload release non riuscito');
      }
    );
  }

  formatFileSize(bytes: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  salva(): void {
    if (this.alertAbilitato && !this.setup.email_destinatari?.trim()) {
      this.addMessage('warn', 'Attenzione', 'Inserire almeno un destinatario email');
      return;
    }
    if (this.alertPlaylistAbilitato && !this.setup.email_destinatari?.trim()) {
      this.addMessage('warn', 'Attenzione', 'Inserire almeno un destinatario email per l\'alert playlist');
      return;
    }

    const payload = this.buildSavePayload();

    this.isSaving = true;
    this.apiService.post('kodi/monitor_setup_update', payload).subscribe(
      () => {
        this.isSaving = false;
        this.smtpPassword = '';
        this.addMessage('success', 'Successo', 'Impostazioni salvate');
        this.loadSetup(false);
      },
      () => {
        this.isSaving = false;
        this.addMessage('error', 'Errore', 'Salvataggio non riuscito');
      }
    );
  }

  inviaTestEmail(): void {
    if (!this.testEmail?.trim()) {
      this.addMessage('warn', 'Attenzione', 'Inserire un indirizzo email per il test');
      return;
    }

    if (!this.alertAbilitato) {
      this.addMessage(
        'warn',
        'Attenzione',
        'Attiva "Abilita invio email di alert" e salva prima di inviare il test'
      );
      return;
    }

    if (!this.setup.smtp_host?.trim() || !this.setup.email_from?.trim()) {
      this.addMessage('warn', 'Attenzione', 'Compilare almeno host SMTP e email mittente');
      return;
    }

    this.isTesting = true;
    const payload = {
      ...this.buildSavePayload(),
      destinatario_test: this.testEmail.trim(),
    };

    this.apiService.post('kodi/monitor_setup_testemail', payload).subscribe(
      (data: any) => {
        this.isTesting = false;
        const result = this.extractResultItem(data);
        const esito = result?.esito;
        const messaggio = result?.messaggio || 'Email di prova inviata';
        if (esito === 'errore') {
          this.addMessage('error', 'Errore', messaggio);
        } else {
          this.addMessage('success', 'Successo', messaggio);
        }
      },
      () => {
        this.isTesting = false;
        this.addMessage('error', 'Errore', 'Invio email di prova non riuscito');
      }
    );
  }

  private applySetup(row: any): void {
    const clean = { ...row };
    delete clean.smtp_password;

    this.alertAbilitato = this.readAlertAbilitato(clean);
    this.alertPlaylistAbilitato = this.readAlertPlaylistAbilitato(clean);

    this.setup = {
      ...this.setup,
      ...clean,
      alert_abilitato: this.alertAbilitato,
      alert_playlist_abilitato: this.alertPlaylistAbilitato,
      smtp_port: clean.smtp_port != null ? Number(clean.smtp_port) : this.setup.smtp_port,
      minuti_inattivita:
        clean.minuti_inattivita != null ? Number(clean.minuti_inattivita) : this.setup.minuti_inattivita,
      ripeti_ogni_minuti:
        clean.ripeti_ogni_minuti != null ? Number(clean.ripeti_ogni_minuti) : this.setup.ripeti_ogni_minuti,
      minuti_playlist_ferma:
        clean.minuti_playlist_ferma != null
          ? Number(clean.minuti_playlist_ferma)
          : this.setup.minuti_playlist_ferma,
      ripeti_ogni_minuti_playlist:
        clean.ripeti_ogni_minuti_playlist != null
          ? Number(clean.ripeti_ogni_minuti_playlist)
          : this.setup.ripeti_ogni_minuti_playlist,
    };
    this.smtpPasswordSet = !!clean.smtp_password_set;
    this.smtpPassword = '';
    if (!this.setup.smtp_encryption) {
      this.setup.smtp_encryption = 'tls';
    }
  }

  private readAlertAbilitato(row: any): boolean {
    if (!row) {
      return false;
    }

    const keys = ['alert_abilitato', 'alertAbilitato', 'alert_enabled', 'abilita_alert'];
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        return this.toBool(row[key]);
      }
    }

    return false;
  }

  private readAlertPlaylistAbilitato(row: any): boolean {
    if (!row) {
      return false;
    }

    const keys = [
      'alert_playlist_abilitato',
      'alertPlaylistAbilitato',
      'alert_playlist_enabled',
      'abilita_alert_playlist',
    ];
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        return this.toBool(row[key]);
      }
    }

    return false;
  }

  private buildSavePayload(): any {
    const payload = { ...this.setup };
    delete payload.smtp_password_set;
    const alertValue = this.alertAbilitato ? 1 : 0;
    payload.alert_abilitato = alertValue;
    payload.alertAbilitato = alertValue;
    const alertPlaylistValue = this.alertPlaylistAbilitato ? 1 : 0;
    payload.alert_playlist_abilitato = alertPlaylistValue;
    payload.alertPlaylistAbilitato = alertPlaylistValue;
    if (this.smtpPassword) {
      payload.smtp_password = this.smtpPassword;
    }
    return payload;
  }

  private extractResultItem(data: any): any {
    if (!data?.Result) {
      return null;
    }
    if (Array.isArray(data.Result)) {
      return data.Result[0] || null;
    }
    return data.Result;
  }

  private extractSetupRow(data: any): any {
    if (!data?.Result) {
      return null;
    }
    if (Array.isArray(data.Result)) {
      return data.Result[0] || null;
    }
    return data.Result;
  }

  private toBool(value: any): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'S' || value === 's') {
      return true;
    }
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'N' || value === 'n') {
      return false;
    }
    return !!value;
  }

  private addMessage(severity: string, summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail });
  }
}
